/**
 * @file Admin dashboard UI — pages/Dashboard/Admin/ManufacturingProcesses/AdminManufacturingProcesses.tsx.
 */

import React, { useCallback, useEffect, useMemo, useState } from "react";
import axiosClient from "../../../../api/axiosClient";
import Button from "../../../../components/common/Button";
import OutlineButton from "../../../../components/common/OutlineButton";
import {
  manufacturingProcessesAdminService,
  type ManufacturingProcessAdmin,
} from "../../../../services/manufacturingProcessesAdminService";
import "../Admin.css";
import "../CharacterizationFactors/AdminCharacterizationFactors.css";

type MaterialOption = { id: number; name: string; code?: string };

const emptyForm = () => ({
  materialId: "",
  name: "",
  isActive: true,
});

const AdminManufacturingProcesses: React.FC = () => {
  const [rows, setRows] = useState<ManufacturingProcessAdmin[]>([]);
  const [materials, setMaterials] = useState<MaterialOption[]>([]);
  const [filterMaterialId, setFilterMaterialId] = useState<string>("");
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [success, setSuccess] = useState<string | null>(null);

  const [modalOpen, setModalOpen] = useState(false);
  const [editingId, setEditingId] = useState<number | null>(null);
  const [form, setForm] = useState(emptyForm);

  const [deleteTarget, setDeleteTarget] =
    useState<ManufacturingProcessAdmin | null>(null);

  useEffect(() => {
    let cancelled = false;
    (async () => {
      try {
        const res = await axiosClient.get("/materials");
        const data = res.data?.data;
        if (!cancelled && res.data?.success && Array.isArray(data)) {
          setMaterials(
            data.map((m: { id: number; name: string; code?: string }) => ({
              id: m.id,
              name: m.name,
              code: m.code,
            }))
          );
        }
      } catch {
        if (!cancelled) setMaterials([]);
      }
    })();
    return () => {
      cancelled = true;
    };
  }, []);

  const load = useCallback(async () => {
    setError(null);
    setLoading(true);
    try {
      const mid =
        filterMaterialId === ""
          ? undefined
          : Number.parseInt(filterMaterialId, 10);
      const data = await manufacturingProcessesAdminService.list(
        mid != null && Number.isFinite(mid) && mid > 0 ? mid : undefined
      );
      setRows(data);
    } catch (e: unknown) {
      setError(
        e instanceof Error
          ? e.message
          : "Failed to load manufacturing processes"
      );
      setRows([]);
    } finally {
      setLoading(false);
    }
  }, [filterMaterialId]);

  useEffect(() => {
    load();
  }, [load]);

  const sortedRows = useMemo(
    () =>
      [...rows].sort((a, b) => {
        const m = (a.materialId ?? 0) - (b.materialId ?? 0);
        if (m !== 0) return m;
        const o = (a.sortOrder ?? 0) - (b.sortOrder ?? 0);
        return o !== 0 ? o : a.id - b.id;
      }),
    [rows]
  );

  const materialLabel = (mId: number) => {
    const m = materials.find((x) => x.id === mId);
    if (m) return m.code ? `${m.name} (${m.code})` : m.name;
    return `Material #${mId}`;
  };

  const openCreate = () => {
    setEditingId(null);
    setForm({
      ...emptyForm(),
      materialId: filterMaterialId || "",
    });
    setError(null);
    setModalOpen(true);
  };

  const openEdit = (r: ManufacturingProcessAdmin) => {
    setEditingId(r.id);
    setForm({
      materialId: String(r.materialId ?? ""),
      name: r.name ?? "",
      isActive: Boolean(r.isActive),
    });
    setError(null);
    setModalOpen(true);
  };

  const closeModal = () => {
    setModalOpen(false);
    setEditingId(null);
    setError(null);
  };

  const handleSave = async () => {
    const name = form.name.trim();
    const materialId = Number.parseInt(String(form.materialId).trim(), 10);

    if (!Number.isFinite(materialId) || materialId <= 0) {
      setError("Material is required.");
      return;
    }
    if (!name) {
      setError("Process name is required.");
      return;
    }

    setSaving(true);
    setError(null);
    setSuccess(null);
    try {
      if (editingId == null) {
        await manufacturingProcessesAdminService.create({
          materialId,
          name,
          isActive: form.isActive,
        });
        setSuccess("Manufacturing process created.");
      } else {
        await manufacturingProcessesAdminService.update(editingId, {
          materialId,
          name,
          isActive: form.isActive,
        });
        setSuccess("Manufacturing process updated.");
      }
      closeModal();
      await load();
      setTimeout(() => setSuccess(null), 4000);
    } catch (e: unknown) {
      setError(e instanceof Error ? e.message : "Save failed");
    } finally {
      setSaving(false);
    }
  };

  const confirmDelete = async () => {
    if (!deleteTarget) return;
    setSaving(true);
    setError(null);
    try {
      await manufacturingProcessesAdminService.delete(deleteTarget.id);
      setSuccess("Deleted.");
      setDeleteTarget(null);
      await load();
      setTimeout(() => setSuccess(null), 4000);
    } catch (e: unknown) {
      setError(e instanceof Error ? e.message : "Delete failed");
    } finally {
      setSaving(false);
    }
  };

  return (
    <div className="background-form-container analysis-shell admin-char-factors">
      <div className="analysis-banner">
        <span className="analysis-banner__title">Manufacturing processes</span>
      </div>

      <div className="admin-form-root">
        <div className="dataset-container">
          <div className="dataset-section">
            <h3 className="dataset-section-title">
              Manage manufacturing process steps
            </h3>
            <p className="muted" style={{ marginBottom: "1rem" }}>
              Admin CRUD on <code>GET/POST/PUT/DELETE /admin/manufacturing-processes</code>.
              Active steps are exposed publicly as{" "}
              <code>GET /manufacturing-processes?materialId=</code> (inactive
              rows are hidden there but listed here).
            </p>

            {error && !modalOpen && !deleteTarget && (
              <div className="error-message" style={{ marginBottom: 12 }}>
                <span className="error-icon">⚠️</span>
                <span>{error}</span>
              </div>
            )}
            {success && (
              <div className="success-message" style={{ marginBottom: 12 }}>
                <span className="success-icon">✅</span>
                <span>{success}</span>
              </div>
            )}

            <div
              className="char-factors-toolbar"
              style={{
                flexWrap: "wrap",
                gap: 12,
                alignItems: "center",
              }}
            >
              <label className="muted" style={{ display: "flex", gap: 8, alignItems: "center" }}>
                Filter by material
                <select
                  className="data-table-filter-select"
                  value={filterMaterialId}
                  onChange={(e) => setFilterMaterialId(e.target.value)}
                  disabled={loading}
                >
                  <option value="">All materials</option>
                  {materials.map((m) => (
                    <option key={m.id} value={String(m.id)}>
                      {m.code ? `${m.name} (${m.code})` : m.name}
                    </option>
                  ))}
                </select>
              </label>
              <Button
                label="Add row"
                type="button"
                onClick={openCreate}
                disabled={loading}
              />
            </div>

            {loading ? (
              <p>Loading…</p>
            ) : sortedRows.length === 0 ? (
              <p className="muted">
                No rows for this filter. Click &quot;Add row&quot; to create one.
              </p>
            ) : (
              <div className="char-factors-table-wrap">
                <table>
                  <thead>
                    <tr>
                      <th>Material</th>
                      <th>Process name</th>
                      <th>Status</th>
                      <th>Updated</th>
                      <th className="cell-actions">Actions</th>
                    </tr>
                  </thead>
                  <tbody>
                    {sortedRows.map((r) => (
                      <tr key={r.id}>
                        <td>
                          {r.material?.name
                            ? r.material.code
                              ? `${r.material.name} (${r.material.code})`
                              : r.material.name
                            : materialLabel(r.materialId)}
                        </td>
                        <td>{r.name}</td>
                        <td>
                          {r.isActive ? (
                            <span>Active</span>
                          ) : (
                            <span className="badge-inactive">Inactive</span>
                          )}
                        </td>
                        <td className="muted" style={{ fontSize: "0.8rem" }}>
                          {r.updatedAt
                            ? new Date(r.updatedAt).toLocaleString()
                            : "—"}
                        </td>
                        <td className="cell-actions">
                          <OutlineButton
                            label="Edit"
                            type="button"
                            onClick={() => openEdit(r)}
                          />
                          <OutlineButton
                            label="Delete"
                            type="button"
                            onClick={() => {
                              setError(null);
                              setDeleteTarget(r);
                            }}
                          />
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            )}
          </div>
        </div>
      </div>

      {modalOpen && (
        <div
          className="modal-backdrop"
          role="presentation"
          onClick={(e) => {
            if (e.target === e.currentTarget) closeModal();
          }}
        >
          <div
            className="modal-panel"
            role="dialog"
            aria-labelledby="mp-modal-title"
          >
            <div className="modal-header" id="mp-modal-title">
              {editingId == null
                ? "New manufacturing process"
                : "Edit manufacturing process"}
            </div>
            <div className="modal-body">
              {error && (
                <div className="error-message">
                  <span className="error-icon">⚠️</span>
                  <span>{error}</span>
                </div>
              )}
              <div>
                <label htmlFor="mp-materialId">Material *</label>
                <select
                  id="mp-materialId"
                  value={form.materialId}
                  onChange={(e) =>
                    setForm((f) => ({ ...f, materialId: e.target.value }))
                  }
                  disabled={saving}
                >
                  <option value="">Select material…</option>
                  {materials.map((m) => (
                    <option key={m.id} value={String(m.id)}>
                      {m.code ? `${m.name} (${m.code})` : m.name}
                    </option>
                  ))}
                </select>
                <p className="muted">From GET /materials (same as contributor forms).</p>
              </div>
              <div>
                <label htmlFor="mp-name">Process name *</label>
                <input
                  id="mp-name"
                  type="text"
                  value={form.name}
                  onChange={(e) =>
                    setForm((f) => ({ ...f, name: e.target.value }))
                  }
                  disabled={saving}
                  placeholder="e.g. Crushing"
                />
                <p className="muted">
                  Unique per material (409 if duplicate name).
                </p>
              </div>
              <div className="checkbox-row">
                <input
                  id="mp-active"
                  type="checkbox"
                  checked={form.isActive}
                  onChange={(e) =>
                    setForm((f) => ({ ...f, isActive: e.target.checked }))
                  }
                  disabled={saving}
                />
                <label htmlFor="mp-active">
                  Active (visible on public API for this material)
                </label>
              </div>
            </div>
            <div className="modal-footer">
              <OutlineButton
                label="Cancel"
                type="button"
                onClick={closeModal}
                disabled={saving}
              />
              <Button
                label={saving ? "Saving…" : "Save"}
                type="button"
                onClick={handleSave}
                disabled={saving}
              />
            </div>
          </div>
        </div>
      )}

      {deleteTarget && (
        <div
          className="modal-backdrop"
          role="presentation"
          onClick={(e) => {
            if (e.target === e.currentTarget && !saving) setDeleteTarget(null);
          }}
        >
          <div
            className="modal-panel"
            role="alertdialog"
            aria-labelledby="mp-del-title"
          >
            <div className="modal-header" id="mp-del-title">
              Delete manufacturing process?
            </div>
            <div className="modal-body">
              <p>
                Remove <strong>{deleteTarget.name}</strong> for{" "}
                <strong>
                  {deleteTarget.material?.name
                    ? deleteTarget.material.code
                      ? `${deleteTarget.material.name} (${deleteTarget.material.code})`
                      : deleteTarget.material.name
                    : materialLabel(deleteTarget.materialId)}
                </strong>
                ?
              </p>
              {error && (
                <div className="error-message">
                  <span className="error-icon">⚠️</span>
                  <span>{error}</span>
                </div>
              )}
            </div>
            <div className="modal-footer">
              <OutlineButton
                label="Cancel"
                type="button"
                onClick={() => setDeleteTarget(null)}
                disabled={saving}
              />
              <Button
                label={saving ? "Deleting…" : "Delete"}
                type="button"
                onClick={confirmDelete}
                disabled={saving}
              />
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

/** React component — Admin Manufacturing Processes. */
export default AdminManufacturingProcesses;
